import type { CostBidderOption, CostContract, CostRfq, CostRfqAward, CostRfqFilter, CostRfqQuote, CostRfqStatus } from '../types'
import { compareMoneyStrings } from './contracts'
import type { ModuleActions } from './permissions'
import { normalizeSearch } from './tree'

type T = (key: string, opts?: Record<string, unknown>) => string

// ---- Register filter (part D spec §8) ----

export interface RfqFilterState { status: CostRfqStatus | null; search: string }
export const EMPTY_RFQ_FILTER: RfqFilterState = { status: null, search: '' }

/** The API filter: only the fields that are set; `search` is the debounced text. */
export function toRfqFilter(s: RfqFilterState, search: string): CostRfqFilter {
  const f: CostRfqFilter = {}
  if (s.status) f.status = s.status
  if (search.trim()) f.search = search.trim()
  return f
}
export const isRfqFiltered = (s: RfqFilterState): boolean => !!(s.status || s.search.trim())

// ---- Rights (spec §3, §6) ----

export interface RfqAffordances {
  create: boolean
  edit: boolean
  editNode: boolean
  cancel: boolean
  reopen: boolean
  remove: boolean
  editQuotes: boolean
  award: boolean
  revoke: boolean
  addAttachment: boolean
  removeAttachment: boolean
}

/** The server enforces the same rules; this only hides what it would refuse. `r` is null on the register. */
export function rfqAffordances(a: ModuleActions, r: Pick<CostRfq, 'status'> | null): RfqAffordances {
  const open = r?.status === 'OPEN'
  return {
    create: a.canCreate,
    edit: a.canEdit && !!r,
    editNode: a.canEdit && open,
    cancel: a.canEdit && open,
    reopen: a.canEdit && r?.status === 'CANCELLED',
    remove: a.canDelete && !!r && r.status !== 'AWARDED',
    editQuotes: a.canEdit && open,
    award: a.canCreate && open,
    revoke: a.canDelete && r?.status === 'AWARDED',
    addAttachment: a.canCreate && !!r,
    removeAttachment: a.canDelete && !!r,
  }
}

/** "Nabídky přijaty" (spec §3), derived: an open RFQ where every asked supplier has an amount. */
export const allQuoted = (r: Pick<CostRfq, 'status' | 'askedCount' | 'quotedCount'>): boolean =>
  r.status === 'OPEN' && r.askedCount > 0 && r.quotedCount === r.askedCount

/** Spec §8: the due date turns red while the RFQ is open and the date has passed. `today` is YYYY-MM-DD. */
export const isRfqOverdue = (r: Pick<CostRfq, 'status' | 'dueDate'>, today: string): boolean => r.status === 'OPEN' && !!r.dueDate && r.dueDate < today

/** "Nová smlouva", or "Změna – ⟨contract⟩" (spec §8). */
export const rfqTypeText = (r: Pick<CostRfq, 'contractId' | 'contractName' | 'contractNumber'>, t: T): string =>
  r.contractId
    ? t('CostControl_rfq_type_change_of', { contract: r.contractNumber ? `${r.contractNumber} · ${r.contractName}` : r.contractName })
    : t('CostControl_rfq_type_CONTRACT')

// ---- Header form ----

export type RfqKind = 'CONTRACT' | 'CHANGE'
export interface RfqForm { kind: RfqKind; title: string; description: string; dueDate: string | null; nodeId: string | null; contractId: string | null }
export const emptyRfqForm = (): RfqForm => ({ kind: 'CONTRACT', title: '', description: '', dueDate: null, nodeId: null, contractId: null })
export const formFromRfq = (r: CostRfq): RfqForm => ({
  kind: r.contractId ? 'CHANGE' : 'CONTRACT', title: r.title, description: r.description ?? '', dueDate: r.dueDate, nodeId: r.nodeId, contractId: r.contractId,
})

/** The first problem as an i18n key (the server checks the same), or null. */
export function rfqFormProblem(f: RfqForm): string | null {
  if (!f.title.trim()) return 'CostControl_error_title_required'
  if (f.kind === 'CHANGE' && !f.contractId) return 'CostControl_error_change_contract_required'
  return null
}

/**
 * Form → API input. Create sends the type (a contract only for a change RFQ); edit never does (spec §3: fixed at
 * creation), and sends the budget item only when it changed on an OPEN RFQ, so editing a title never trips the lock.
 */
export function rfqInput(f: RfqForm, mode: 'create' | 'edit', original?: Pick<CostRfq, 'status' | 'nodeId'> | null): Record<string, unknown> {
  const base = { title: f.title.trim(), description: f.description.trim() || null, dueDate: f.dueDate }
  if (mode === 'create') return { ...base, nodeId: f.nodeId, contractId: f.kind === 'CHANGE' ? f.contractId : null }
  return original?.status === 'OPEN' && f.nodeId !== original.nodeId ? { ...base, nodeId: f.nodeId } : base
}

/** Spec §3: a change RFQ targets an active subcontract, the only kind a contract change can be drafted on. */
export const rfqContractOptions = <C extends Pick<CostContract, 'kind' | 'status'>>(contracts: C[]): C[] =>
  contracts.filter((c) => c.kind === 'SUB' && c.status === 'ACTIVE')

// ---- Quotes (spec §3, §8) ----

export interface QuoteForm {
  supplierName: string
  supplierIco: string
  bidderId: string | null
  amount: string | null
  amountValid: boolean
  receivedAt: string | null
  note: string
}
/** A new supplier; a change RFQ pre-fills its first one with the contract's counterparty (spec §8). */
export const emptyQuoteForm = (prefill?: { name: string; ico: string | null } | null): QuoteForm => ({
  supplierName: prefill?.name ?? '', supplierIco: prefill?.ico ?? '', bidderId: null, amount: null, amountValid: true, receivedAt: null, note: '',
})
export const formFromQuote = (q: CostRfqQuote): QuoteForm => ({
  supplierName: q.supplierName, supplierIco: q.supplierIco ?? '', bidderId: q.bidderId, amount: q.amount, amountValid: true, receivedAt: q.receivedAt, note: q.note ?? '',
})

/** The first problem as an i18n key (the server checks the same), or null. An empty amount is an asked supplier. */
export function quoteFormProblem(f: QuoteForm): string | null {
  if (!f.supplierName.trim()) return 'CostControl_error_supplier_required'
  if (!f.amountValid) return 'CostControl_error_bad_amount'
  if (f.amount !== null && compareMoneyStrings(f.amount, '0') < 0) return 'CostControl_error_negative_amount'
  return null
}
export const quoteInput = (f: QuoteForm): Record<string, unknown> => ({
  supplierName: f.supplierName.trim(), supplierIco: f.supplierIco.trim() || null, bidderId: f.bidderId, amount: f.amount, receivedAt: f.receivedAt, note: f.note.trim() || null,
})

/** The first supplier of a change RFQ: the target contract's counterparty, when the contract list knows it. */
export function quotePrefill(rfq: Pick<CostRfq, 'contractId'>, quoteCount: number, contracts: Pick<CostContract, 'id' | 'counterpartyName' | 'counterpartyIco'>[]) {
  if (!rfq.contractId || quoteCount > 0) return null
  const c = contracts.find((x) => x.id === rfq.contractId)
  return c ? { name: c.counterpartyName, ico: c.counterpartyIco } : null
}

/** "Vybrat vítěze" on a quote: the award right and an amount above zero (spec §4; the server refuses anything else). */
export const canAwardQuote = (af: Pick<RfqAffordances, 'award'>, q: Pick<CostRfqQuote, 'amount'>): boolean =>
  af.award && q.amount !== null && compareMoneyStrings(q.amount, '0') > 0

/** Spec §6: the directory's suppliers for the picker, not deleted, matching the search without diacritics, by name. */
export function bidderOptions(bidders: CostBidderOption[], query: string): CostBidderOption[] {
  const q = normalizeSearch(query)
  return bidders.filter((b) => !b.deleted && (!q || normalizeSearch(b.name).includes(q))).sort((a, b) => a.name.localeCompare(b.name, 'cs'))
}

/** Spec §4: the UI opens the award's result. */
export const awardResultPath = (projectId: string, award: Pick<CostRfqAward, 'contractId' | 'changeId'>): string | null =>
  award.contractId ? `/project/${projectId}/cost-control/contracts/${award.contractId}`
    : award.changeId ? `/project/${projectId}/cost-control/changes/${award.changeId}` : null
